import React, { useEffect } from 'react';
import { motion } from 'framer-motion';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="w-full bg-brand-cream">
      <section className="pt-40 pb-20 bg-brand-olive text-white text-center">
        <div className="container mx-auto px-6">
          <p className="font-script text-3xl text-brand-cream mb-2">Legal Information</p>
          <h1 className="font-serif text-4xl md:text-6xl uppercase tracking-[0.2em]">Privacy Policy</h1>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-6 max-w-4xl">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white p-8 md:p-16 shadow-2xl border border-brand-olive/5"
          >
            <p className="text-brand-olive/50 text-[10px] font-black uppercase tracking-widest mb-8">
              Last Updated: May 20, 2024
            </p>

            <div className="prose prose-olive max-w-none space-y-10 text-brand-olive/80 leading-relaxed font-light">
              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Introduction</h2>
                <p>
                  R. Venue Apartelle respects your privacy and is committed to protecting the personal information you share with us. This Privacy Policy explains how we collect, use, store, and protect your information when you visit our website, submit an inquiry, make a booking request, or communicate with us.
                </p>
                <p className="mt-4">
                  By using this website, you agree to the collection and use of information in accordance with this Privacy Policy.
                </p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Information We Collect</h2>
                <div className="space-y-6">
                  <div>
                    <h3 className="font-bold text-brand-olive text-sm uppercase tracking-wider mb-2">1. Personal Information</h3>
                    <p>This may include your name, email address, phone number, booking details, preferred dates, number of guests, and any message or request you submit through our website or communication channels.</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-olive text-sm uppercase tracking-wider mb-2">2. Booking and Inquiry Details</h3>
                    <p>When you inquire about availability or make a booking request, we may collect details related to your stay, such as check-in date, check-out date, room preference, guest count, and special requests.</p>
                  </div>
                  <div>
                    <h3 className="font-bold text-brand-olive text-sm uppercase tracking-wider mb-2">3. Technical Information</h3>
                    <p>Our website may collect basic technical information such as browser type, device type, pages visited, and general website usage information. This helps us improve the website experience.</p>
                  </div>
                </div>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">How We Use Your Information</h2>
                <p>We use your information to:</p>
                <ul className="list-disc pl-5 mt-4 space-y-2">
                  <li>Respond to inquiries and booking requests</li>
                  <li>Confirm availability and accommodation details</li>
                  <li>Communicate important information about your stay</li>
                  <li>Provide customer support</li>
                  <li>Improve our website and guest experience</li>
                  <li>Maintain internal records</li>
                  <li>Comply with legal, safety, or business requirements</li>
                </ul>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Sharing of Information</h2>
                <p>We do not sell your personal information.</p>
                <p className="mt-4">We may share your information only when necessary, such as:</p>
                <ul className="list-disc pl-5 mt-4 space-y-2">
                  <li>With authorized staff or service providers who help us manage bookings and guest communication</li>
                  <li>When required by law, regulation, or legal process</li>
                  <li>When needed to protect the safety, rights, or property of R. Venue Apartelle, our guests, or others</li>
                </ul>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Third-Party Services</h2>
                <p>Our website may use third-party tools or services for booking communication, email sending, website hosting, analytics, reviews, or other website functions. These third-party services may process limited information only as needed to provide their services.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Data Security</h2>
                <p>We take reasonable steps to protect your personal information from unauthorized access, loss, misuse, alteration, or disclosure. However, no online system can guarantee absolute security.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Data Retention</h2>
                <p>We keep your personal information only for as long as necessary to fulfill the purpose for which it was collected, support booking records, comply with legal obligations, resolve disputes, or maintain business records.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Your Rights</h2>
                <p>You may request access to, correction of, or deletion of your personal information, subject to applicable laws and legitimate business or legal requirements.</p>
                <p className="mt-4">To make a privacy-related request, please contact us using the details below.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Cookies and Website Tracking</h2>
                <p>Our website may use cookies or similar technologies to improve website functionality and understand how visitors use the site. You may adjust your browser settings to refuse cookies, but some parts of the website may not work properly.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Children’s Privacy</h2>
                <p>Our website is not intended to collect personal information from children without the consent or supervision of a parent or guardian.</p>
              </div>

              <div>
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Updates to This Privacy Policy</h2>
                <p>We may update this Privacy Policy from time to time. Any updates will be posted on this page with the updated date.</p>
              </div>

              <div className="pt-10 border-t border-brand-olive/10">
                <h2 className="font-serif text-2xl text-brand-olive uppercase tracking-widest mb-4">Contact Us</h2>
                <div className="bg-brand-cream/30 p-6 border-l-4 border-brand-olive">
                  <p className="font-bold">R. Venue Apartelle</p>
                  <p>Lucban, Quezon, Philippines</p>
                  <p>Email: <a href="mailto:rvenueapartelle@lucban.ph" className="underline hover:text-brand-dark">rvenueapartelle@lucban.ph</a></p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default PrivacyPolicy;